// Enhanced Guest Check-in with Event Context
// File: guest-frontend/src/components/EventAwareCheckIn.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL as API_BASE } from '../utils/apiBase';
import GuestCheckIn from './GuestCheckIn';
import SelfServiceRegistration from './SelfServiceRegistration';
import EventProgram from './EventProgram';
import './EventAwareCheckIn.css';

const API_BASE_URL = API_BASE;

export default function EventAwareCheckIn() {
  const { eventPath } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const portalToken = searchParams.get('portal_token') || searchParams.get('portalToken') || null;
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const registrationStageParam = (searchParams.get('stage') || '').toLowerCase();
  const isPreEventStage = registrationStageParam === 'pre-event';
  const [activeMode, setActiveMode] = useState(isPreEventStage ? 'register' : 'checkin'); // checkin, register, program
  const [forceRegisterView, setForceRegisterView] = useState(false);
  const justRegistered = searchParams.get('registered') === '1';
  const [showRegistrationBanner, setShowRegistrationBanner] = useState(justRegistered);

  const loadEventByPath = useCallback(async () => {
    setLoading(true);
    setError('');

    const modeParam = (searchParams.get('mode') || '').toLowerCase();
    const path = location?.pathname || '';
    const programRequested = modeParam === 'program' || /\/program\/?$/i.test(path);
    const registerRequested = modeParam === 'register' || /\/register\/?$/i.test(path);

    const applyMode = () => {
      if (isPreEventStage) {
        setActiveMode('register');
        setForceRegisterView(true);
        return;
      }
      if (programRequested) {
        setActiveMode('program');
        setForceRegisterView(false);
      } else if (registerRequested) {
        setActiveMode('register');
        setForceRegisterView(true);
      } else {
        setActiveMode('checkin');
        setForceRegisterView(false);
      }
    };

    const setGlobalEvent = (title) => {
      const globalEvent = {
        id: null,
        name: title,
        event_path: 'global',
        event_settings: {},
        check_in_config: {},
        self_service_enabled: false
      };
      setEvent(globalEvent);
      applyMode();
    };
    
    if (!eventPath) {
      setGlobalEvent('Global Check-in');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/events/by-path/${eventPath}`);
      setEvent(response.data);
      applyMode();
    } catch (error) {
      if (error.response?.status === 404) {
        setGlobalEvent('Event Check-in');
        setError('Event not found. Showing general check-in.');
      } else {
        setError('Unable to load event. Some features may be unavailable.');
        setGlobalEvent('Event Check-in');
      }
    } finally {
      setLoading(false);
    }
  }, [eventPath, isPreEventStage, location?.pathname, searchParams]);

  useEffect(() => {
    loadEventByPath();
  }, [loadEventByPath]);

  useEffect(() => {
    if (justRegistered) {
      setShowRegistrationBanner(true);
      try {
        const params = new URLSearchParams(searchParams);
        params.delete('registered');
        const newQuery = params.toString();
        const newUrl = newQuery ? `${location.pathname}?${newQuery}` : location.pathname;
        window.history.replaceState({}, '', newUrl);
      } catch (_) {
        // ignore history update
      }
      const timeout = setTimeout(() => setShowRegistrationBanner(false), 6000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [justRegistered, location.pathname, searchParams]);

  if (loading) {
    return (
      <div className="event-loading">
        <div className="loading-spinner"></div>
        <p>Loading event...</p>
      </div>
    );
  }

  // In global/fallback mode we don't block the UI on error; show a soft banner instead

  const registrationMode = (event?.registration_mode || 'standard').toLowerCase();
  const eventConfig = {
    eventTitle: event.name,
    eventPath: event.event_path,
    eventSettings: event.event_settings || {},
    checkInConfig: event.check_in_config || {},
    eventId: event.id,
    registrationMode
  };

  return (
    <div className="event-aware-container">
      {/* Event Header */}
      <div className="event-header">
        <div className="event-branding">
          {eventConfig.eventSettings.branding?.logo_url && (
            <img 
              src={eventConfig.eventSettings.branding.logo_url} 
              alt={eventConfig.eventTitle}
              className="event-logo"
            />
          )}
          <h1 className="event-title">{eventConfig.eventTitle}</h1>
        </div>

        {error && (
          <div className="soft-error" role="status" aria-live="polite">
            {error}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="event-navigation" role="tablist" aria-label="Guest navigation">
          {!isPreEventStage && (
            <button 
              className={`nav-tab ${activeMode === 'checkin' ? 'active' : ''}`}
              onClick={() => setActiveMode('checkin')}
              title="Check-in"
              aria-label="Check-in"
              role="tab"
              aria-selected={activeMode === 'checkin'}
            >
              <span className="nav-label">Check-in</span>
            </button>
          )}
          
          {(event.self_service_enabled || forceRegisterView) && (
            <button 
              className={`nav-tab ${activeMode === 'register' ? 'active' : ''}`}
              onClick={() => setActiveMode('register')}
              title="Register"
              aria-label="Register"
              role="tab"
              aria-selected={activeMode === 'register'}
              disabled={!event.self_service_enabled}
            >
              <span className="nav-label">Register</span>
            </button>
          )}
          
          {!isPreEventStage && (
            <button 
              className={`nav-tab ${activeMode === 'program' ? 'active' : ''}`}
              onClick={() => setActiveMode('program')}
              title="Program"
              aria-label="Program"
              role="tab"
              aria-selected={activeMode === 'program'}
            >
              <span className="nav-label">Program</span>
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="event-content">
        {activeMode === 'checkin' && !isPreEventStage && (
          <>
            {showRegistrationBanner && (
              <div className="registration-banner">
                ✅ Registration received! Please continue with check-in
                {registrationStageParam === 'onsite' ? ' so we can seat you right away.' : ' to complete your arrival.'}
              </div>
            )}
            <GuestCheckIn 
              eventConfig={eventConfig}
              eventId={event.id}
              portalToken={portalToken}
              registrationMode={registrationMode}
            />
          </>
        )}
        
        {activeMode === 'register' && (event.self_service_enabled || forceRegisterView) && (
          <SelfServiceRegistration 
            eventConfig={eventConfig}
            eventId={event.id}
            prefetchedEvent={event}
          />
        )}
        
        {activeMode === 'program' && !isPreEventStage && (
          <EventProgram 
            eventConfig={eventConfig}
            eventId={event.id}
          />
        )}
      </div>

    </div>
  );
}
